/**
 * PropertySubmitPage - Form for agents to submit new properties.
 *
 * Thin wrapper around PropertyForm that handles navigation.
 *
 * Role-based behavior:
 * - Agent/Admin only: Can create new properties
 * - Investors: See Access Restricted page
 *
 * MRG-281: external agents reach this page during the guided training
 * tour. The form itself is real — submitting creates a real listing —
 * and the user's action here closes the training:
 *
 * - "Submit to Investors" → completion modal "Congrats…"
 * - "Save Draft" → completion modal "You're close…"
 * - "Cancel" → completion modal "You've completed your training!"
 *
 * The actual `complete-training` API call is fired by
 * `useAgentTrainingTourContext().requestCompletion(...)` (and
 * pre-emptively by PropertyForm before the gate-blocking submit).
 */

import { useEffect, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { PropertyForm } from '@/features/properties/components/PropertyForm'
import type {
  PropertyResponse,
  PropertyStatus,
} from '@/features/properties/api/properties'
import {
  SubmitPageGuide,
  useAgentTrainingTourContext,
} from '@/features/agent-training-tour'

export const Route = createFileRoute({
  component: PropertySubmitPage,
})

function PropertySubmitPage() {
  const navigate = useNavigate()
  const { roles } = useAuth()
  const { needsExternalAgentTerms } = useUserProfile()
  const { isTrainingMode, currentPhase, requestCompletion } =
    useAgentTrainingTourContext()

  // Check if user has agent or admin role
  const canCreateProperties = roles.includes('agent') || roles.includes('admin')

  // MRG-281: external agents who haven't signed the ToS yet can't reach the
  // form. The training-completion gate is intentionally skipped here:
  // during the guided tour we want the agent to actually fill out the
  // real form. PropertyForm preemptively flips the training gate before
  // POSTing so the API gate doesn't block the submit.
  useEffect(() => {
    if (canCreateProperties && needsExternalAgentTerms) {
      navigate({ to: '/onboarding', replace: true })
    }
  }, [canCreateProperties, needsExternalAgentTerms, navigate])

  // MRG-281: detect "exit" path — agent landed on this page during
  // training and is now navigating away without submitting / drafting.
  // The hasActedRef gate avoids firing the exit completion when the
  // user successfully submits / saves a draft (in which case
  // requestCompletion has already been called from handleSuccess).
  const hasActedRef = useRef(false)
  // Track whether the user actually walked through the submit-actions
  // popover (the second submit-page tooltip explaining Submit / Save
  // Draft). Without this gate, the unmount cleanup would fire the
  // "exit" completion when:
  //   - the tour navigates the user BACK from submit-intro → step-5
  //   - React's strict-mode mount→unmount→mount dev cycle runs
  // Only after the user reaches submit-actions are they considered
  // "free to interact" and a subsequent navigation away counts as
  // a real exit per the Figma flow.
  const reachedSubmitActionsRef = useRef(false)
  useEffect(() => {
    if (currentPhase === 'submit-actions') {
      reachedSubmitActionsRef.current = true
    }
  }, [currentPhase])

  useEffect(() => {
    return () => {
      if (
        isTrainingMode
        && !hasActedRef.current
        && reachedSubmitActionsRef.current
      ) {
        requestCompletion('exit')
      }
    }
    // requestCompletion / isTrainingMode are stable enough; we only want
    // this teardown to fire on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSuccess = (property: PropertyResponse, status: PropertyStatus) => {
    if (isTrainingMode) {
      hasActedRef.current = true
      // Submit-to-investors → 'available'; everything else (notably
      // 'draft') → "you're close" copy.
      requestCompletion(status === 'draft' ? 'draft' : 'submitted')
      // Still navigate so the agent lands on a useful page after the
      // completion modal closes (modal is purely informational).
      navigate({ to: '/properties/$id', params: { id: property.id } })
      return
    }
    navigate({ to: '/properties/$id', params: { id: property.id } })
  }

  const handleCancel = () => {
    if (isTrainingMode) {
      hasActedRef.current = true
      requestCompletion('exit')
    }
    navigate({ to: '/properties' })
  }

  // Non-agent/admin unauthorized view
  if (!canCreateProperties) {
    return (
      <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            Create Property
          </Text>
          <Text variant="bodySm" color="muted">
            Submit a new property
          </Text>
        </Stack>
        <Stack gap="16" align="center" justify="center" style={{ flex: 1 }}>
          <Icon name="lock" size={48} style={{ color: '#999' }} />
          <Text as="h2" variant="bodyLg" weight="bold">
            Access Restricted
          </Text>
          <Text variant="body" color="muted" style={{ textAlign: 'center', maxWidth: 400 }}>
            Property creation is only available to agents.
          </Text>
          <Link to="/properties">
            <Button>Go to Properties</Button>
          </Link>
        </Stack>
      </Stack>
    )
  }

  // While redirect-to-onboarding is queued (ToS not yet signed), suppress
  // the form to avoid a flash.
  if (needsExternalAgentTerms) {
    return null
  }

  return (
    <>
      <PropertyForm
        mode="create"
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
      {isTrainingMode && <SubmitPageGuide />}
    </>
  )
}
